import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';

import {
  ILoyaltyServiceTransactionsQuery,
  useLoyaltyServiceTransactionsLazyQuery,
  useLoyaltyUserTransactionsQuery,
} from 'generated/graphql-gateway';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';

import { mapLegacyTransaction, mapServiceTransaction } from './transaction-adapters';

interface UseLoyaltyTransactionsOptions {
  legacyFetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
}

interface ServiceTransactionsState {
  data?: ILoyaltyServiceTransactionsQuery;
  error?: ApolloError;
  identity?: string;
  loading: boolean;
}

const INITIAL_SERVICE_STATE: ServiceTransactionsState = { loading: false };

const toApolloError = (error: unknown): ApolloError =>
  error instanceof ApolloError
    ? error
    : new ApolloError({
        errorMessage: error instanceof Error ? error.message : String(error),
      });

export const useLoyaltyTransactions = ({
  legacyFetchPolicy = 'cache-first',
  skip = false,
}: UseLoyaltyTransactionsOptions = {}) => {
  const { loyaltyUserId, loading: authLoading } = useLoyaltyUserId();
  const enableLoyaltyServiceTransactions = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_TRANSACTIONS)
  );
  const shouldSkip = skip || !loyaltyUserId;

  const legacyQuery = useLoyaltyUserTransactionsQuery({
    fetchPolicy: legacyFetchPolicy,
    variables: { loyaltyId: loyaltyUserId ?? '' },
    skip: shouldSkip || enableLoyaltyServiceTransactions,
  });
  const [executeServiceTransactions] = useLoyaltyServiceTransactionsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [serviceState, setServiceState] = useState<ServiceTransactionsState>(INITIAL_SERVICE_STATE);
  const serviceRequestId = useRef(0);
  const serviceAbortController = useRef<AbortController | null>(null);

  const cancelServiceRequest = useCallback(() => {
    serviceRequestId.current += 1;
    serviceAbortController.current?.abort();
    serviceAbortController.current = null;
  }, []);

  const runServiceQuery = useCallback(
    (identity: string) => {
      const requestId = serviceRequestId.current + 1;
      serviceRequestId.current = requestId;
      serviceAbortController.current?.abort();
      const abortController = new AbortController();
      serviceAbortController.current = abortController;

      setServiceState(currentState => ({
        data: currentState.identity === identity ? currentState.data : undefined,
        error: undefined,
        identity,
        loading: true,
      }));

      const request = executeServiceTransactions({
        context: {
          fetchOptions: { signal: abortController.signal },
          queryDeduplication: false,
        },
      }).then(result => {
        if (result.error) {
          throw result.error;
        }

        return result;
      });

      void request.then(
        result => {
          if (serviceRequestId.current === requestId && !abortController.signal.aborted) {
            setServiceState({
              data: result.data,
              error: undefined,
              identity,
              loading: false,
            });
          }
        },
        error => {
          if (serviceRequestId.current === requestId && !abortController.signal.aborted) {
            setServiceState(currentState => ({
              data: currentState.identity === identity ? currentState.data : undefined,
              error: toApolloError(error),
              identity,
              loading: false,
            }));
          }
        }
      );

      return request;
    },
    [executeServiceTransactions]
  );

  useEffect(() => {
    if (shouldSkip || !enableLoyaltyServiceTransactions || !loyaltyUserId) {
      cancelServiceRequest();
      setServiceState(INITIAL_SERVICE_STATE);
      return;
    }

    void runServiceQuery(loyaltyUserId).catch(() => undefined);
    return cancelServiceRequest;
  }, [
    cancelServiceRequest,
    enableLoyaltyServiceTransactions,
    loyaltyUserId,
    runServiceQuery,
    shouldSkip,
  ]);

  const legacyTransactions = legacyQuery.data?.loyaltyUserV2?.transactions;
  const serviceStateMatchesIdentity = Boolean(
    loyaltyUserId && serviceState.identity === loyaltyUserId
  );
  const serviceTransactions = serviceStateMatchesIdentity
    ? serviceState.data?.loyaltyServiceTransactions?.items
    : undefined;
  const serviceLoading = !shouldSkip && (!serviceStateMatchesIdentity || serviceState.loading);
  const activeTransactions = enableLoyaltyServiceTransactions
    ? serviceTransactions
    : legacyTransactions;
  const activeLoading = enableLoyaltyServiceTransactions ? serviceLoading : legacyQuery.loading;
  const activeError = enableLoyaltyServiceTransactions
    ? serviceStateMatchesIdentity
      ? serviceState.error
      : undefined
    : legacyQuery.error;
  const loyaltyIdentityLoading = !loyaltyUserId && authLoading;

  const transactions = useMemo(
    () =>
      enableLoyaltyServiceTransactions
        ? (serviceTransactions ?? []).map(mapServiceTransaction)
        : (legacyTransactions ?? []).map(mapLegacyTransaction),
    [enableLoyaltyServiceTransactions, legacyTransactions, serviceTransactions]
  );

  const { refetch: refetchLegacyTransactions } = legacyQuery;
  const refetch = useCallback(() => {
    if (shouldSkip) {
      return Promise.resolve();
    }

    if (enableLoyaltyServiceTransactions) {
      return runServiceQuery(loyaltyUserId ?? '');
    }

    return refetchLegacyTransactions({ loyaltyId: loyaltyUserId ?? '' });
  }, [
    enableLoyaltyServiceTransactions,
    loyaltyUserId,
    refetchLegacyTransactions,
    runServiceQuery,
    shouldSkip,
  ]);

  return {
    transactions,
    error: activeError,
    initialLoading: loyaltyIdentityLoading || (activeLoading && !activeTransactions),
    loading: loyaltyIdentityLoading || activeLoading,
    refetch,
  };
};
